import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useMotionValue, animate } from 'motion/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText as GSAPSplitText } from 'gsap/SplitText';
import { useGSAP } from '@gsap/react';
import './standardCards.css';

gsap.registerPlugin(ScrollTrigger, GSAPSplitText, useGSAP);

const cn = (...classes) => classes.filter(Boolean).join(' ');

function Table({ className, ...props }) {
  return (
    <div data-slot="table-container" className={cn('invexis-table-container', className)}>
      <table data-slot="table" className={cn('invexis-table')} {...props} />
    </div>
  );
}

function TableHeader({ className, ...props }) {
  return (
    <thead data-slot="table-header" className={cn('invexis-table-header', className)} {...props} />
  );
}

function TableBody({ className, ...props }) {
  return (
    <tbody data-slot="table-body" className={cn('invexis-table-body', className)} {...props} />
  );
}

function TableFooter({ className, ...props }) {
  return (
    <tfoot data-slot="table-footer" className={cn('invexis-table-footer', className)} {...props} />
  );
}

function TableRow({ className, ...props }) {
  return (
    <tr data-slot="table-row" className={cn('invexis-table-row', className)} {...props} />
  );
}

function TableHead({ className, ...props }) {
  return (
    <th data-slot="table-head" className={cn('invexis-table-head', className)} {...props} />
  );
}

function TableCell({ className, ...props }) {
  return (
    <td data-slot="table-cell" className={cn('invexis-table-cell', className)} {...props} />
  );
}

function TableCaption({ className, ...props }) {
  return (
    <caption data-slot="table-caption" className={cn('invexis-table-caption', className)} {...props} />
  );
}

// Inject global styles to prevent autocomplete yellow highlighting
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = `
    /* Prevent autocomplete yellow highlighting globally */
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      background-color: var(--invexis-autofill-bg, transparent) !important;
      -webkit-box-shadow: 0 0 0 1000px var(--invexis-autofill-bg, transparent) inset !important;
      box-shadow: 0 0 0 1000px var(--invexis-autofill-bg, transparent) inset !important;
      -webkit-text-fill-color: currentColor !important;
      caret-color: currentColor !important;
      transition: background-color 999999s ease-out 0s !important;
    }

    textarea:-webkit-autofill,
    textarea:-webkit-autofill:hover,
    textarea:-webkit-autofill:focus,
    textarea:-webkit-autofill:active,
    select:-webkit-autofill,
    select:-webkit-autofill:hover,
    select:-webkit-autofill:focus,
    select:-webkit-autofill:active {
      background-color: var(--invexis-autofill-bg, transparent) !important;
      -webkit-box-shadow: 0 0 0 1000px var(--invexis-autofill-bg, transparent) inset !important;
      box-shadow: 0 0 0 1000px var(--invexis-autofill-bg, transparent) inset !important;
      -webkit-text-fill-color: currentColor !important;
      caret-color: currentColor !important;
      transition: background-color 999999s ease-out 0s !important;
    }

    input:-moz-autofill,
    textarea:-moz-autofill,
    select:-moz-autofill {
      box-shadow: 0 0 0 1000px var(--invexis-autofill-bg, transparent) inset !important;
      caret-color: currentColor !important;
    }
    
    /* Remove autocomplete dropdown arrow */
    input::-webkit-calendar-picker-indicator {
      display: none !important;
    }
    
    /* Ensure consistent search input styling */
    input[type="search"] {
      -webkit-appearance: none;
      appearance: none;
    }
    
    /* Remove all borders and outlines for inputs globally - EXCEPT password/reset password inputs */
    input[type="text"]:not(.input),
    input[type="search"],
    input:not([type="password"]):not([type="text"]):not(.input) {
      
      
      box-shadow: none !important;
      background-color: transparent !important;
    }
    
    /* Specific override for inputs within StandardSearch component */
    div[style*="borderRadius"] input:not(.input),
    .search-container input,
    .search-input {
      outline: none !important;
      border: none !important;
      box-shadow: none !important;
      background-color: transparent !important;
    }
  `;
  if (!document.head.querySelector('[data-autocomplete-fix]')) {
    styleSheet.setAttribute('data-autocomplete-fix', 'true');
    document.head.appendChild(styleSheet);
  }
  
  // Inject SplitText anti-blur styles
  const splitTextStyles = document.createElement("style");
  splitTextStyles.type = "text/css";
  splitTextStyles.innerText = `
    /* Prevent blur in SplitText animations */
    .split-parent,
    .split-char,
    .split-word,
    .split-line {
      -webkit-font-smoothing: antialiased !important;
      -moz-osx-font-smoothing: grayscale !important;
      text-rendering: optimizeLegibility !important;
      transform: translateZ(0) !important;
      backface-visibility: hidden !important;
      perspective: 1000px !important;
      will-change: transform, opacity !important;
    }
    
    .split-char,
    .split-word {
      display: inline-block !important;
    }
  `;
  if (!document.head.querySelector('[data-splittext-fix]')) {
    splitTextStyles.setAttribute('data-splittext-fix', 'true');
    document.head.appendChild(splitTextStyles);
  }
}

// Standardized Search Component (based on abc.js implementation)
export const StandardSearch = ({ 
  value,
  onChange, 
  onClear, 
  placeholder = "Search...", 
  style = {}, 
  inputRef = null,
  showClearButton = true,
  onKeyDown = null,
  animatedFocus = false,
  onFocusChange = null,
  rightContent = null, // New prop for additional content on the right
  // Legacy prop support
  searchTerm,
  onSearchChange,
  onClearSearch
}) => {
  // Support both new and legacy prop names
  const searchValue = value !== undefined ? value : searchTerm;
  const handleChange = onChange || onSearchChange;
  const handleClear = onClear || onClearSearch;
  const hasSearchText = Boolean((searchValue ?? '').toString().trim().length > 0);
  const showClearControl = showClearButton && hasSearchText;
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const baseShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
  const hoverShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
  const focusShadow = "0 0 0 3px rgba(59, 130, 246, 0.12), 0 6px 14px rgba(15, 23, 42, 0.16)";
  const currentShadow = animatedFocus
    ? (isFocused ? focusShadow : isHovered ? hoverShadow : baseShadow)
    : baseShadow;

  const handleInputFocus = () => {
    if (!animatedFocus) return;
    setIsFocused(true);
    if (onFocusChange) onFocusChange(true);
  };

  const handleInputBlur = () => {
    if (!animatedFocus) return;
    setIsFocused(false);
    if (onFocusChange) onFocusChange(false);
  };

  return (
    <div 
      style={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "white",
        borderRadius: "10px",
        padding: "12px 16px 12px 16px",
        marginBottom: "12px",
        border: animatedFocus ? (isFocused ? "1px solid rgba(59, 130, 246, 0.28)" : "1px solid transparent") : "none",
        boxShadow: currentShadow,
        transform: animatedFocus && isFocused ? "translateY(-1px)" : "translateY(0)",
        transition: animatedFocus ? "box-shadow 0.2s ease, border-color 0.2s ease, transform 0.18s ease" : "box-shadow 0.2s ease",
        cursor: "text",
        ...style
      }}
      onClick={() => {
        if (inputRef && inputRef.current) {
          inputRef.current.focus();
        }
      }}
      onMouseEnter={(e) => {
        if (animatedFocus) {
          setIsHovered(true);
          return;
        }
        e.currentTarget.style.boxShadow = hoverShadow;
      }}
      onMouseLeave={(e) => {
        if (animatedFocus) {
          setIsHovered(false);
          return;
        }
        e.currentTarget.style.boxShadow = baseShadow;
      }}
    >
      {/* Search Icon */}
      <svg 
        width="16" 
        height="16" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="#999" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{
          marginRight: "12px",
          opacity: animatedFocus ? (isFocused ? 1 : 0.74) : 1,
          transform: animatedFocus ? `scale(${isFocused ? 1.04 : 1})` : "none",
          transition: "opacity 0.15s ease, transform 0.15s ease"
        }}
      >
        <circle cx="11" cy="11" r="8"></circle>
        <path d="m21 21-4.35-4.35"></path>
      </svg>
      
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={searchValue || ''}
        onChange={(e) => {
          if (handleChange) {
            if (onChange) {
              // New API: pass value directly
              handleChange(e.target.value);
            } else {
              // Legacy API: pass event
              handleChange(e);
            }
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && handleClear) {
            handleClear();
          }
          if (onKeyDown) {
            onKeyDown(e);
          }
        }}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-form-type="other"
        style={{
          flex: 1,
          border: "none",
          background: "transparent",
          fontSize: "16px",
          outline: "none",
          boxShadow: "none",
          color: "#000",
          fontFamily: "inherit",
          lineHeight: "1.4",
          // Prevent autocomplete yellow highlighting
          WebkitBoxShadow: "0 0 0 1000px var(--invexis-autofill-bg, var(--invexis-input-bg, transparent)) inset",
          WebkitTextFillColor: "currentColor",
          transition: animatedFocus ? "color 0.15s ease" : "none"
        }}
      />
      
      {(showClearControl || rightContent) && (
        <motion.div
          key={showClearControl ? 'search-clear-control' : 'search-right-control'}
          initial={animatedFocus ? { opacity: 0, scale: 0.92 } : false}
          animate={{ opacity: 1, scale: 1 }}
          transition={animatedFocus ? { duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] } : { duration: 0 }}
          style={{
            marginLeft: "8px",
            marginRight: "6px",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}
        >
          {showClearControl && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear search"
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "2px",
                borderRadius: "999px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.15s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#999"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: "block" }}
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
          {rightContent}
        </motion.div>
      )}
    </div>
  );
};

// Standardized Scrollbar Styles Component (System Default with Arrows)
export const StandardScrollStyles = () => (
  <style>{`
    /* Global: thin, light scrollbars (matches report screens) */
    * {
      scrollbar-width: thin; /* Firefox */
      scrollbar-color: #cfd8dc #f0f2f5; /* thumb, track */
    }

    *::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    *::-webkit-scrollbar-track {
      background: #f0f2f5;
    }

    *::-webkit-scrollbar-thumb {
      background-color: #cfd8dc;
      border-radius: 8px;
    }

    *::-webkit-scrollbar-thumb:hover {
      background-color: #b0bec5;
    }

    /* Standard scrollbar utility for scrollable containers */
    .standard-scrollbar {
      overflow: auto;
      scrollbar-width: thin;
      scrollbar-color: #cfd8dc #f0f2f5;
    }

    /* Invisible scrollbars when explicitly desired */
    .hidden-scrollbar {
      scrollbar-width: none;
      -ms-overflow-style: none;
    }

    .hidden-scrollbar::-webkit-scrollbar {
      display: none;
    }

    /* Alternative darker theme (kept for opt-in use) */
    .dark-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: #6b7280 #374151;
    }

    .dark-scrollbar::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    .dark-scrollbar::-webkit-scrollbar-track {
      background: #374151;
      border-radius: 3px;
    }

    .dark-scrollbar::-webkit-scrollbar-thumb {
      background-color: #6b7280;
      border-radius: 3px;
      transition: background-color 0.2s ease;
    }

    .dark-scrollbar::-webkit-scrollbar-thumb:hover {
      background-color: #9ca3af;
    }
  `}</style>
);

// Highlight matching text utility function (global style)
export const highlightMatch = (text, searchTerm) => {
  const baseStyle = { fontWeight: 500, fontSize: 'inherit', lineHeight: 1.4, color: 'inherit' };
  // Strong but clean highlight that's visible on white and colored cards
  const matchStyle = {
    fontWeight: 'bold',
    color: '#111',
    fontSize: 'inherit',
  };

  const str = `${text ?? ''}`;
  if (!searchTerm || !searchTerm.trim()) {
    return <span style={baseStyle}>{str}</span>;
  }

  // Escape regex special chars in searchTerm for safe matching
  const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = str.split(regex);

  // Using index parity: odd indices are matches due to capturing group
  return parts.map((part, index) => (
    <span key={index} style={index % 2 === 1 ? { ...baseStyle, ...matchStyle } : baseStyle}>
      {part}
    </span>
  ));
};

// Smart search function (priority-based filtering)
export const smartSearch = (items, searchTerm, searchFields = ['name']) => {
  if (!searchTerm.trim()) return items;
  
  const searchLower = searchTerm.toLowerCase();
  
  // Priority 1: Items starting with search term in primary field
  const startsWithMatches = items.filter(item => {
    const primaryField = item[searchFields[0]] || '';
    return primaryField.toLowerCase().startsWith(searchLower);
  });
  
  // Priority 2: Items containing search term in primary field (but not starting with it)
  const containsMatches = items.filter(item => {
    const primaryField = item[searchFields[0]] || '';
    return primaryField.toLowerCase().includes(searchLower) &&
           !primaryField.toLowerCase().startsWith(searchLower);
  });
  
  // Priority 3: Items matching other fields
  const otherMatches = items.filter(item => {
    const primaryField = item[searchFields[0]] || '';
    if (primaryField.toLowerCase().includes(searchLower)) return false;
    
    return searchFields.slice(1).some(field => {
      const fieldValue = item[field] || '';
      return fieldValue.toLowerCase().includes(searchLower);
    });
  });
  
  return [...startsWithMatches, ...containsMatches, ...otherMatches];
};

// Smart search function (priority-based filtering) - keeping both names for compatibility
export const performSmartSearch = (items, searchTerm, searchFields = ['name']) => {
  return smartSearch(items, searchTerm, searchFields);
};

// Standard line height utilities
export const standardLineHeights = {
  tight: 1.2,
  normal: 1.4,
  relaxed: 1.6,
  loose: 1.8
};

// Standard text sizing with proper line heights
export const standardTextSizes = {
  xs: { fontSize: '12px', lineHeight: standardLineHeights.normal },
  sm: { fontSize: '14px', lineHeight: standardLineHeights.normal },
  base: { fontSize: '16px', lineHeight: standardLineHeights.normal },
  lg: { fontSize: '18px', lineHeight: standardLineHeights.normal },
  xl: { fontSize: '20px', lineHeight: standardLineHeights.tight },
  '2xl': { fontSize: '24px', lineHeight: standardLineHeights.tight },
  '3xl': { fontSize: '30px', lineHeight: standardLineHeights.tight }
};

// Default text sizes (alias for base size)
export const defaultTextSizes = standardTextSizes.base;

// Inline scrollbar styles for direct use in style objects (System Default with Arrows)
export const standardScrollStyles = {
  overflowY: 'auto',
  overflowX: 'auto',
  scrollbarWidth: 'thin',
  scrollbarColor: '#cfd8dc #f0f2f5'
};

// ============================================================
// SPLIT TEXT ANIMATION COMPONENT
// GSAP-powered text reveal animation with scroll trigger
// ============================================================
export const SplitText = ({
  text,
  className = '',
  style = {},
  delay = 50,
  duration = 1.25,
  ease = 'power3.out',
  splitType = 'chars',
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  threshold = 0.1,
  rootMargin = '-100px',
  textAlign = 'center',
  tag = 'p',
  onLetterAnimationComplete
}) => {
  const ref = useRef(null);
  const animationCompletedRef = useRef(false);
  const onCompleteRef = useRef(onLetterAnimationComplete);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Keep callback ref updated
  useEffect(() => {
    onCompleteRef.current = onLetterAnimationComplete;
  }, [onLetterAnimationComplete]);

  useEffect(() => {
    if (document.fonts.status === 'loaded') {
      setFontsLoaded(true);
    } else {
      document.fonts.ready.then(() => {
        setFontsLoaded(true);
      });
    }
  }, []);

  useGSAP(
    () => {
      if (!ref.current || !text || !fontsLoaded) return;
      // Prevent re-animation if already completed
      if (animationCompletedRef.current) return;
      const el = ref.current;

      if (el._rbsplitInstance) {
        try {
          el._rbsplitInstance.revert();
        } catch (_) {
          /* noop */
        }
        el._rbsplitInstance = null;
      }

      const startPct = (1 - threshold) * 100;
      const marginMatch = /^(-?\d+(?:\.\d+)?)(px|em|rem|%)?$/.exec(rootMargin);
      const marginValue = marginMatch ? parseFloat(marginMatch[1]) : 0;
      const marginUnit = marginMatch ? marginMatch[2] || 'px' : 'px';
      const sign =
        marginValue === 0
          ? ''
          : marginValue < 0
            ? `-=${Math.abs(marginValue)}${marginUnit}`
            : `+=${marginValue}${marginUnit}`;
      const start = `top ${startPct}%${sign}`;

      let targets;
      const assignTargets = self => {
        if (splitType.includes('chars') && self.chars.length) targets = self.chars;
        if (!targets && splitType.includes('words') && self.words.length) targets = self.words;
        if (!targets && splitType.includes('lines') && self.lines.length) targets = self.lines;
        if (!targets) targets = self.chars || self.words || self.lines;
      };

      const splitInstance = new GSAPSplitText(el, {
        type: splitType,
        smartWrap: true,
        autoSplit: splitType === 'lines',
        linesClass: 'split-line',
        wordsClass: 'split-word',
        charsClass: 'split-char',
        reduceWhiteSpace: false,
        onSplit: self => {
          assignTargets(self);
          
          // Set initial styles to prevent blur
          gsap.set(targets, {
            transformPerspective: 1000,
            transformStyle: 'preserve-3d',
            backfaceVisibility: 'hidden'
          });
          
          const tween = gsap.fromTo(
            targets,
            { ...from, force3D: true },
            {
              ...to,
              duration,
              ease,
              stagger: delay / 1000,
              scrollTrigger: {
                trigger: el,
                start,
                once: true,
                fastScrollEnd: true,
                anticipatePin: 0.4
              },
              onComplete: () => {
                animationCompletedRef.current = true;
                onCompleteRef.current?.();
                // Clear transforms after animation to prevent ongoing blur
                gsap.set(targets, { clearProps: 'transform' });
              },
              willChange: 'transform, opacity',
              force3D: true,
              transformPerspective: 1000
            }
          );
          return tween;
        }
      });

      el._rbsplitInstance = splitInstance;

      return () => {
        ScrollTrigger.getAll().forEach(st => {
          if (st.trigger === el) st.kill();
        });
        try {
          splitInstance.revert();
        } catch (_) {
          /* noop */
        }
        el._rbsplitInstance = null;
      };
    },
    {
      dependencies: [
        text,
        delay,
        duration,
        ease,
        splitType,
        JSON.stringify(from),
        JSON.stringify(to),
        threshold,
        rootMargin,
        fontsLoaded
      ],
      scope: ref
    }
  );

  const renderTag = () => {
    const defaultStyle = {
      textAlign,
      overflow: 'hidden',
      display: 'inline-block',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      willChange: 'transform, opacity',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      textRendering: 'optimizeLegibility',
      transform: 'translateZ(0)',
      backfaceVisibility: 'hidden',
      perspective: 1000
    };
    const mergedStyle = { ...defaultStyle, ...style };
    const classes = `split-parent ${className}`;
    const Tag = tag || 'p';

    return (
      <Tag ref={ref} style={mergedStyle} className={classes}>
        {text}
      </Tag>
    );
  };
  return renderTag();
};

// Export card components
export { StandardCard, ActionCard, InfoCard, DataCard } from './standardCards.js';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption
};

// ============================================================
// INVEXIS DASHBOARD CAROUSEL
// Framer-motion draggable carousel. Patch into standardStyles.
// ============================================================
const INV_CAROUSEL_GAP = 16;
const INV_SPRING_OPTIONS = { type: 'tween', ease: 'easeOut', duration: 0.3 };
const INV_DRAG_BUFFER = 18;
const INV_VELOCITY_THRESHOLD = 500;

const InvCarouselItem = React.memo(function InvCarouselItem({ item, index, itemWidth, transition, isActive }) {
  return (
    <motion.div
      className="inv-carousel-item"
      style={{
        width: itemWidth,
        touchAction: 'pan-y',
        pointerEvents: isActive ? 'auto' : 'none'
      }}
      transition={transition}
    >
      {item.content ? (
        item.content
      ) : (
        <div className="inv-carousel-item-inner">
          {item.icon && (
            <div style={{ marginBottom: '16px' }}>{item.icon}</div>
          )}
          {item.title && (
            <div style={{ fontWeight: 700, fontSize: '18px', marginBottom: '8px', color: '#111' }}>
              {item.title}
            </div>
          )}
          {item.description && (
            <p style={{ fontSize: '14px', color: '#555', margin: 0 }}>{item.description}</p>
          )}
        </div>
      )}
    </motion.div>
  );
});

export function DashboardCarousel({
  items = [],
  baseWidth = 0,
  autoplay = false,
  autoplayDelay = 3000,
  pauseOnHover = false,
  loop = false,
  onChange = null,
  initialIndex = 0
}) {
  const outerRef = useRef(null);
  const containerRef = useRef(null);
  const [measuredWidth, setMeasuredWidth] = useState(300);

  // Auto-measure container width when baseWidth is not provided
  useEffect(() => {
    if (baseWidth) {
      setMeasuredWidth(baseWidth);
      return;
    }
    const el = outerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      if (entries[0]) setMeasuredWidth(entries[0].contentRect.width);
    });
    observer.observe(el);
    // Initial measurement
    setMeasuredWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [baseWidth]);

  const effectiveWidth = baseWidth || measuredWidth;
  const itemWidth = effectiveWidth;
  const trackItemOffset = itemWidth + INV_CAROUSEL_GAP;

  const itemsForRender = useMemo(() => {
    if (!loop) return items;
    if (items.length === 0) return [];
    return [items[items.length - 1], ...items, items[0]];
  }, [items, loop]);

  const normalizedInitialIndex = useMemo(() => {
    const parsedIndex = Number(initialIndex);
    const safeIndex = Number.isFinite(parsedIndex) ? Math.floor(parsedIndex) : 0;

    if (items.length === 0) {
      return 0;
    }

    return Math.max(0, Math.min(safeIndex, items.length - 1));
  }, [initialIndex, items.length]);

  const startPosition = loop ? normalizedInitialIndex + 1 : normalizedInitialIndex;

  const [position, setPosition] = useState(startPosition);
  const x = useMotionValue(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isJumping, setIsJumping] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const activeIndex = items.length === 0
    ? 0
    : loop
      ? (position - 1 + items.length) % items.length
      : Math.min(position, items.length - 1);

  // Notify parent of active slide change
  useEffect(() => {
    if (onChange) onChange(activeIndex);
  }, [activeIndex, onChange]);

  // Hover pause
  useEffect(() => {
    if (!pauseOnHover || !containerRef.current) return;
    const el = containerRef.current;
    const enter = () => setIsHovered(true);
    const leave = () => setIsHovered(false);
    el.addEventListener('mouseenter', enter);
    el.addEventListener('mouseleave', leave);
    return () => {
      el.removeEventListener('mouseenter', enter);
      el.removeEventListener('mouseleave', leave);
    };
  }, [pauseOnHover]);

  // Autoplay
  useEffect(() => {
    if (!autoplay || itemsForRender.length <= 1) return;
    if (pauseOnHover && isHovered) return;
    const timer = setInterval(() => {
      setPosition(prev => {
        if (!loop && prev >= itemsForRender.length - 1) return prev;
        return Math.min(prev + 1, itemsForRender.length - 1);
      });
    }, autoplayDelay);
    return () => clearInterval(timer);
  }, [autoplay, autoplayDelay, isHovered, pauseOnHover, itemsForRender.length, loop]);

  // Reset when item count/width/initial index context changes
  useEffect(() => {
    setPosition(startPosition);
    x.set(-startPosition * trackItemOffset);
  }, [startPosition, trackItemOffset, x]);

  useEffect(() => {
    if (!loop && position > itemsForRender.length - 1) {
      setPosition(Math.max(0, itemsForRender.length - 1));
    }
  }, [itemsForRender.length, loop, position]);

  const effectiveTransition = isJumping ? { duration: 0 } : INV_SPRING_OPTIONS;

  const handleAnimationComplete = () => {
    if (!loop || itemsForRender.length <= 1) { setIsAnimating(false); return; }
    const lastClone = itemsForRender.length - 1;
    if (position === lastClone) {
      setIsJumping(true);
      setPosition(1);
      x.set(-1 * trackItemOffset);
      requestAnimationFrame(() => { setIsJumping(false); setIsAnimating(false); });
      return;
    }
    if (position === 0) {
      setIsJumping(true);
      const target = items.length;
      setPosition(target);
      x.set(-target * trackItemOffset);
      requestAnimationFrame(() => { setIsJumping(false); setIsAnimating(false); });
      return;
    }
    setIsAnimating(false);
  };

  const handleDragEnd = (_, info) => {
    const { offset, velocity } = info;
    const dir =
      offset.x < -INV_DRAG_BUFFER || velocity.x < -INV_VELOCITY_THRESHOLD ? 1
      : offset.x > INV_DRAG_BUFFER || velocity.x > INV_VELOCITY_THRESHOLD ? -1
      : 0;
    if (dir === 0) {
      animate(x, -(position * trackItemOffset), INV_SPRING_OPTIONS);
      return;
    }
    setPosition(prev => Math.max(0, Math.min(prev + dir, itemsForRender.length - 1)));
  };

  const dragProps = loop ? {} : {
    dragConstraints: {
      left: -trackItemOffset * Math.max(itemsForRender.length - 1, 0),
      right: 0
    }
  };

  return (
    <div ref={outerRef} className="inv-carousel-outer">
      <div
        ref={containerRef}
        className="inv-carousel-container"
        style={{ width: effectiveWidth ? `${effectiveWidth}px` : '100%' }}
      >
        <div className="inv-carousel-track-clip">
        <motion.div
          className="inv-carousel-track"
          drag={isJumping ? false : 'x'}
          dragDirectionLock={false}
          dragMomentum={false}
          {...dragProps}
          style={{
            width: itemWidth,
            gap: `${INV_CAROUSEL_GAP}px`,
            x,
            touchAction: 'pan-y'
          }}
          onDragEnd={handleDragEnd}
          animate={{ x: -(position * trackItemOffset) }}
          transition={effectiveTransition}
          onAnimationStart={() => setIsAnimating(true)}
          onAnimationComplete={handleAnimationComplete}
        >
          {itemsForRender.map((item, idx) => (
            <InvCarouselItem
              key={`${item?.id ?? idx}-${idx}`}
              item={item}
              index={idx}
              itemWidth={itemWidth}
              transition={effectiveTransition}
              isActive={idx === position}
            />
          ))}
        </motion.div>
        </div>
        <div className="inv-carousel-indicators-container">
          <div className="inv-carousel-indicators">
            {items.map((_, idx) => (
              <motion.div
                key={idx}
                className={`inv-carousel-indicator ${activeIndex === idx ? 'active' : 'inactive'}`}
                animate={{ scale: activeIndex === idx ? 1.2 : 1 }}
                onClick={() => setPosition(loop ? idx + 1 : idx)}
                transition={{ duration: 0.15 }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const standardStylesExports = { 
  StandardSearch, 
  StandardScrollStyles, 
  highlightMatch, 
  smartSearch,
  performSmartSearch, 
  standardLineHeights, 
  standardTextSizes,
  defaultTextSizes,
  standardScrollStyles,
  DashboardCarousel,
  SplitText,
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption
};

export default standardStylesExports;